import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TransactionsLists, TransactionStatus, TransactionType } from 'vopay';
import { useBlockingLoader } from 'shared';
import { Button } from 'UI';
import useAccountBearer from '@/components/Entities/hooks/useApi/useAccountBearer';
import useRefundAccountTransaction from '@/components/Entities/hooks/useApi/useRefundAccountTransaction';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';

/** The rails earthnode's `createRefund` accepts: settled EFT, as the account's old tab also allowed. */
const REFUNDABLE_RAILS: ReadonlySet<string> = new Set([TransactionType.EFT_FUNDING, TransactionType.EFT_WITHDRAWAL]);

type TransactionRefundButtonProps = {
  /** The row the page already read; undefined while it loads, null when it could not be read. */
  transaction: TransactionsLists | null | undefined;
  onRefunded: () => Promise<unknown>;
};

/**
 * Refund, on the transaction page (ID-13369). Offered to a partner with account endpoint access who is
 * not view-only, on a row that reads as a settled, not-yet-refunded EFT transaction.
 *
 * The row is a snapshot, so this is a filter rather than a verdict: earthnode's `createRefund` is the
 * authority — it re-checks the rail and the status — and its refusal is what the error dialog shows.
 * There is no eligibility endpoint to ask up front, as `ResubmitTransactionButton` has in the client
 * portal, so nothing more can be known before the call.
 */
export default function TransactionRefundButton({ transaction, onRefunded }: TransactionRefundButtonProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Transaction.Details.Refund' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { accountId } = useParams<{ accountId: string }>();
  const { bearer } = useAccountBearer(accountId);
  const { canRefundTransactions } = useModuleFeatureFlag('accounts');
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const blockingLoader = useBlockingLoader();
  const { refundAccountTransaction } = useRefundAccountTransaction();

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const isRefundable =
    transaction != null &&
    !transaction.IsRefunded &&
    transaction.TransactionStatus === TransactionStatus.SUCCESSFUL &&
    REFUNDABLE_RAILS.has(transaction.TransactionType);

  if (!canRefundTransactions || isRoleViewOnly || !isRefundable) return null;

  const transactionID = transaction.TransactionID;

  async function handleClick() {
    const result = await alertDialog.open(
      t('title', { defaultValue: 'Refund this transaction?' }),
      t('body', {
        defaultValue: 'Transaction {{transactionID}} will be refunded. This cannot be undone.',
        transactionID,
      }),
      {
        abortLabel: tGlobal('Buttons.cancel'),
        confirmLabel: t('confirm', { defaultValue: 'Refund' }),
      },
    );

    if (result !== 'confirm') return;

    blockingLoader.show();

    try {
      await refundAccountTransaction(transactionID, bearer);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : t('error', { defaultValue: 'The refund could not be processed' });

      alertDialog.error(t('errorTitle', { defaultValue: 'Error! The refund was not successful' }), errorMessage);
      blockingLoader.hide();

      return;
    }

    // The money has moved by here. A re-read that fails leaves the page showing the old row — the hook
    // has logged it — but it must never be reported as a failed refund.
    await onRefunded().catch(() => {});

    toast.success(t('success', { defaultValue: 'Transaction {{transactionID}} was refunded', transactionID }));
    blockingLoader.hide();
  }

  return (
    <Button id={`transaction-refund-${transactionID}`} colorScheme="destructive" onClick={handleClick}>
      {t('confirm', { defaultValue: 'Refund' })}
    </Button>
  );
}
